import React, { useState, useMemo, useEffect, useCallback } from 'react';

import dayjs from 'dayjs';
import { Col, Container, Row, Spinner, Table, Form } from 'react-bootstrap';
import Button from '../../components/Button';
import { FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import Select from 'react-select';

import DeclineReasonModal from './DeclineReasonModal';
import {
  approveTimeOffRequest,
  declineTimeOffRequest,
  resetTimeOffRequest,
  exportTimeOffRequestsToCSV,
  fetchDriverOptions,
} from './operations';
import columns from './utils/tableColumns';
import TOR_STATUSES from './utils/TOR_STATUSES';
import { formatIsoToDate } from '../../utils';
import Check from '../../assets/check.png';
import Close from '../../assets/close.png';
import Excel from '../../assets/excel.png';
import Goto from '../../assets/goto.png';
import Reset from '../../assets/reset.png';
import { TablePagination } from '../../components';

const columnTextNoWrap = 'align-middle text-nowrap text-center';
const defaultCustomHandlers = {};

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const initialSortBy = status => [
  {
    id: status === TOR_STATUSES.SEARCH ? 'startDate' : 'endDate',
    desc: true,
  },
  {
    id: 'createdAt',
    desc: false,
  },
  {
    id: 'employeeName',
    desc: false,
  },
];

const TimeOffRequestsTable = ({
  data = [],
  status = TOR_STATUSES.PENDING_APPROVAL,
  loading = false,
  onDataChange,
  viewAll = false,
  setViewAll,
  isExporting,
  setIsExporting,
  customHandlers = defaultCustomHandlers,
  initialDriverFilter = '',
}) => {
  const [declineModalShow, setDeclineModalShow] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [driverFilter, setDriverFilter] = useState(initialDriverFilter);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);
  const user = useSelector(state => state.admin.user);

  useEffect(() => {
    if (initialDriverFilter) {
      setDriverFilter(initialDriverFilter);
    }
  }, [initialDriverFilter]);

  useEffect(() => {
    let cancelled = false;
    fetchDriverOptions()
      .then(opts => { if (!cancelled) setDriverOptions(opts); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDriversLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const initialExportingState = isExporting !== undefined ? isExporting : false;
  const [localIsExporting, setLocalIsExporting] = useState(initialExportingState);

  const processTimeOffRequest = useCallback(
    async (request, operation, actionType) => {
      setSelectedRequest(request);
      setActionLoading(true);
      setActionError(null);

      try {
        await operation(request);

        if (onDataChange) {
          onDataChange(viewAll, driverFilter);
        }
      } catch (error) {
        console.error(`Error ${actionType.toLowerCase()} request:`, error);
        setActionError(`Failed to ${actionType.toLowerCase()} request. Please try again.`);
      } finally {
        setActionLoading(false);
        setSelectedRequest(null);
      }
    },
    [onDataChange, viewAll, driverFilter],
  );

  const handleApprove = useCallback(
    async request => {
      await processTimeOffRequest(
        { ...request, approvedByUserId: user.id, approvedByUserName: user.name },
        approveTimeOffRequest,
        'Approved',
      );
    },
    [processTimeOffRequest, user],
  );

  const handleDeclineClick = useCallback(request => {
    setSelectedRequest(request);
    setActionError(null);
    setDeclineModalShow(true);
  }, []);

  const handleReset = useCallback(
    async request => {
      await processTimeOffRequest(
        { ...request, resetByUserName: user.name, resetByUserId: user.id },
        resetTimeOffRequest,
        'reset to Pending Approval',
      );
    },
    [processTimeOffRequest, user],
  );

  const handleDeclineSubmit = useCallback(
    async reason => {
      setActionLoading(true);
      setActionError(null);

      try {
        await declineTimeOffRequest({
          ...selectedRequest,
          serviceComment: reason,
          declinedByUserId: user.id,
          declinedByUserName: user.name,
        });

        if (onDataChange) {
          onDataChange(viewAll, driverFilter);
        }
      } catch (error) {
        console.error('Error declining request:', error);
        setActionError('Failed to decline request. Please try again.');
      } finally {
        setActionLoading(false);
        setDeclineModalShow(false);
        setSelectedRequest(null);
      }
    },
    [selectedRequest, onDataChange, viewAll, driverFilter, user],
  );

  const applyDriverFilter = useCallback(
    value => {
      onDataChange(viewAll, value ?? driverFilter);
    },
    [onDataChange, viewAll, driverFilter],
  );

  const handleExportToExcel = useCallback(async () => {
    const setExportingState = setIsExporting || setLocalIsExporting;
    setExportingState(true);

    try {
      await exportTimeOffRequestsToCSV(status, viewAll, driverFilter);
    } catch (error) {
      setActionError('Failed to export data. Please try again.');
    } finally {
      setExportingState(false);
    }
  }, [setIsExporting, setLocalIsExporting, status, viewAll, driverFilter]);

  const tableColumns = useMemo(() => {
    const statusColumns = [...columns[status]];

    if (status === TOR_STATUSES.PENDING_APPROVAL) {
      statusColumns.push(
        {
          Header: 'Approve',
          accessor: 'approve',
          className: columnTextNoWrap,
          Cell: ({ row }) => (
            <img src={Check} alt="Approve" onClick={() => handleApprove(row.original)} style={{ cursor: 'pointer' }} />
          ),
          disableSortBy: true,
        },
        {
          Header: 'Decline',
          accessor: 'decline',
          className: columnTextNoWrap,
          Cell: ({ row }) => (
            <img
              src={Close}
              alt="Decline"
              onClick={() => handleDeclineClick(row.original)}
              style={{ cursor: 'pointer' }}
            />
          ),
          disableSortBy: true,
        },
      );
    } else if (status !== TOR_STATUSES.SEARCH) {
      statusColumns.push({
        Header: 'Status Reset',
        accessor: 'statusReset',
        className: columnTextNoWrap,
        Cell: ({ row }) => {
          // Only show reset button if end date is today or future
          // Parse YYYY-MM-DD as local date (not UTC) to avoid off-by-one in US timezones
          const [year, month, day] = row.original.endDate.split('-').map(Number);
          const endDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (endDate >= today) {
            return (
              <img src={Reset} alt="Reset" onClick={() => handleReset(row.original)} style={{ cursor: 'pointer' }} />
            );
          }
          return null;
        },
        disableSortBy: true,
      });
    } else if (status === TOR_STATUSES.SEARCH && customHandlers.onGoToRequest) {
      statusColumns.push({
        Header: 'Go To Request',
        accessor: 'goToRequest',
        className: columnTextNoWrap,
        Cell: ({ row }) => (
          <img
            src={Goto}
            alt="Go To Request"
            onClick={() => customHandlers.onGoToRequest(row.original)}
            style={{ cursor: 'pointer' }}
          />
        ),
        disableSortBy: true,
      });
    }

    return statusColumns;
  }, [status, handleApprove, handleDeclineClick, handleReset, customHandlers]);

  const tableInstance = useTable(
    {
      columns: tableColumns,
      data,
      initialState: {
        sortBy: initialSortBy(status),
        pageSize: 25,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const { key: groupKey, ...groupProps } = headerGroup.getHeaderGroupProps();
    const headers = headerGroup.headers.map(column => {
      const { key: colKey, ...colProps } = column.getHeaderProps(column.getSortByToggleProps());
      return (
        <th key={colKey} {...colProps} style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {column.render('Header')}
            {column.canSort && !column.disableSortBy ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}
          </span>
        </th>
      );
    });
    return <tr key={groupKey} {...groupProps}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const { key: rowKey, ...rowProps } = row.getRowProps();
    const cells = row.cells.map(cell => {
      const { key: cellKey, ...cellProps } = cell.getCellProps({
        className: cell.column.className,
        style: cell.column.style,
      });
      return (
        <td key={cellKey} {...cellProps}>
          {cell.render('Cell')}
        </td>
      );
    });

    return <tr key={rowKey} {...rowProps}>{cells}</tr>;
  });

  const currentExportingState = isExporting !== undefined ? isExporting : localIsExporting;

  const showFilterAndViewToggle = status !== TOR_STATUSES.SEARCH;

  return (
    <>
      <Container fluid>
        {actionError && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-danger" role="alert">
                {actionError}
              </div>
            </Col>
          </Row>
        )}

        {showFilterAndViewToggle && (
          <Row className="mb-3 align-items-center">
            <Col xs={12} md={6} className="d-flex align-items-center">
              <FaSearch style={{ color: '#fff', backgroundColor: '#c41230', padding: '10px', fontSize: '38px', borderRadius: '4px 0 0 4px', flexShrink: 0 }} />
              <Select
                styles={{
                  control: provided => ({ ...provided, minHeight: '38px', borderRadius: '0 4px 4px 0' }),
                  menuList: provided => ({ ...provided, maxHeight: '200px' }),
                  container: provided => ({ ...provided, width: '435px' }),
                }}
                options={driverOptions}
                isLoading={driversLoading}
                isClearable
                placeholder="Filter by Driver Name & Number"
                onChange={(option, action) => {
                  if (action.action === 'clear') {
                    setDriverFilter('');
                    applyDriverFilter('');
                  }
                  if (action.action === 'select-option') {
                    setDriverFilter(option.value);
                    applyDriverFilter(option.value);
                  }
                }}
                value={driverOptions.find(opt => opt.value === driverFilter) || null}
              />
            </Col>
            <Col xs={12} md={6} className="mt-2 mt-md-0 d-flex justify-content-end">
              {status !== TOR_STATUSES.PENDING_APPROVAL && (
                <Button variant="outline" style={{ marginRight: '1rem' }} onPress={() => setViewAll(!viewAll)} text={viewAll ? 'View Current' : 'View All'} />
              )}

              <img
                onClick={currentExportingState ? null : handleExportToExcel}
                src={Excel}
                alt="export-to-excel"
                style={{
                  cursor: 'pointer',
                  filter: currentExportingState ? 'grayscale(100%) opacity(0.5)' : 'none',
                }}
              />
              {currentExportingState && (
                <Spinner animation="border" size="sm" style={{ position: 'absolute', top: 12, right: 24 }} />
              )}
            </Col>
          </Row>
        )}

        {status === TOR_STATUSES.SEARCH && data.length !== 0 && (
          <Row className="mb-3 align-items-center">
            <Col className="d-flex justify-content-end">
              <img
                role="button"
                tabIndex={0}
                onClick={currentExportingState ? null : (customHandlers.onExportToExcel || handleExportToExcel)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!currentExportingState) (customHandlers.onExportToExcel || handleExportToExcel)(); } }}
                src={Excel}
                alt="Export to Excel"
                style={{
                  cursor: currentExportingState ? 'default' : 'pointer',
                  filter: currentExportingState ? 'grayscale(100%) opacity(0.5)' : 'none',
                }}
              />
              {currentExportingState && (
                <Spinner animation="border" size="sm" style={{ position: 'absolute', top: 12, right: 24 }} />
              )}
            </Col>
          </Row>
        )}
        <Row className="align-items-center">
          <Col className="d-flex justify-content-center justify-content-md-end">
            <TablePagination tableInstance={tableInstance} />
          </Col>
        </Row>
        <Row>
          <Col className="border border-light rounded shadow-sm">
            <Table hover responsive borderless {...getTableProps()}>
              <thead className="text-nowrap">{tableHeaders}</thead>
              <tbody {...getTableBodyProps()}>{tableRows}</tbody>
            </Table>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50px',
              }}>
              {loading && <Spinner animation="border" />}
              {!loading && data.length === 0 && <p>No time off requests found.</p>}
            </div>
          </Col>
        </Row>
      </Container>

      <DeclineReasonModal
        show={declineModalShow}
        onHide={() => setDeclineModalShow(false)}
        onDecline={handleDeclineSubmit}
        loading={actionLoading}
      />
    </>
  );
};

export default TimeOffRequestsTable;
